import { makeAutoObservable, runInAction } from 'mobx';

import { agent } from '../api/agent';
import { toast } from 'react-toastify';
import i18n from '../../i18n';
import { PendingChallengeDto, TeamMatchDto, TeamSearchResultDto } from '../models/Team';



// ── TeamMatchStore ─────────────────────────────────────────────────────────────

export default class TeamMatchStore {
  selectedMatch: TeamMatchDto | undefined = undefined;
  teamMatches: TeamMatchDto[] = [];
  openChallenges: TeamMatchDto[] = [];
  searchResults: TeamSearchResultDto[] = [];
  pendingChallenges: PendingChallengeDto[] = [];
  loadingPendingChallenges = false;

  loading = false;
  loadingInitial = false;
  submitting = false;
  searching = false;

  constructor() { makeAutoObservable(this); }

  // ── UTC helpers ─────────────────────────────────────────────────────────────

  /**
   * Converts a UTC Date from the server into a local Date object.
   * JavaScript Date is always UTC internally — when displayed via
   * toLocaleString() or date-fns format() it automatically shows local time.
   * We just ensure the raw string from the API is properly parsed as UTC.
   */
  private parseUTCDate = (value: Date | string | null | undefined): Date | undefined => {
    if (!value) return undefined;
    // If already a Date object, return as-is (already UTC internally)
    if (value instanceof Date) return value;
    // If string without 'Z', append it so JS parses it as UTC not local
    const str = value.toString();
    const utcStr = str.endsWith('Z') || str.includes('+') ? str : str + 'Z';
    return new Date(utcStr);
  };

  /**
   * Converts a local Date from the UI into a UTC ISO string for the server.
   * new Date() is always UTC internally, toISOString() always returns UTC with 'Z'.
   */
  private toUTCISOString = (value: Date | string | null | undefined): string | undefined => {
    if (!value) return undefined;
    if (value instanceof Date) return value.toISOString();
    return new Date(value).toISOString();
  };

  /**
   * Processes a TeamMatchDto received from the server.
   * Ensures scheduledAt and createdAt are proper Date objects (UTC-aware).
   */
  private processMatch = (match: TeamMatchDto): TeamMatchDto => {
    return {
      ...match,
      scheduledAt: this.parseUTCDate(match.scheduledAt),
      createdAt: this.parseUTCDate(match.createdAt)
    };
  };

  /**
   * Processes a PendingChallengeDto received from the server.
   * Ensures scheduledAt and sentAt are proper Date objects (UTC-aware).
   */
  private processPendingChallenge = (challenge: PendingChallengeDto): PendingChallengeDto => {
    return {
      ...challenge,
      scheduledAt: this.parseUTCDate(challenge.scheduledAt),
      sentAt: this.parseUTCDate(challenge.sentAt) as Date
    };
  };

  // ── Loaders ─────────────────────────────────────────────────────────────────

  loadMatch = async (id: string) => {
    this.loadingInitial = true;
    try {
      const match = await agent.TeamMatches.get(id);
      runInAction(() => {
        this.selectedMatch = this.processMatch(match);
        this.loadingInitial = false;
      });
      return match;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.loadingInitial = false; });
    }
  };

  loadTeamMatches = async (teamId: string) => {
    this.loading = true;
    try {
      const matches = await agent.TeamMatches.getTeamMatches(teamId);
      runInAction(() => {
        this.teamMatches = matches.map(this.processMatch);
        this.loading = false;
      });
    } catch (error) {
      console.error(error);
      runInAction(() => { this.loading = false; });
    }
  };

  loadOpenChallenges = async (sportId: string) => {
    this.loading = true;
    try {
      const challenges = await agent.TeamMatches.getOpenChallenges(sportId);
      runInAction(() => {
        this.openChallenges = challenges.map(this.processMatch);
        this.loading = false;
      });
    } catch (error) {
      console.error(error);
      runInAction(() => { this.loading = false; });
    }
  };

  loadPendingChallenges = async () => {
    this.loadingPendingChallenges = true;
    try {
      const challenges = await agent.TeamMatches.getPendingChallenges();
      runInAction(() => {
        this.pendingChallenges = challenges.map(this.processPendingChallenge);
        this.loadingPendingChallenges = false;
      });
    } catch (error) {
      console.error(error);
      runInAction(() => { this.loadingPendingChallenges = false; });
    }
  };

  // ── Commands ─────────────────────────────────────────────────────────────────

  createDirectChallenge = async (data: {
    teamAId: string; teamBId: string;
    latitude?: number; longitude?: number; scheduledAt?: string;
  }): Promise<string | null> => {
    this.submitting = true;
    try {
      const payload = {
        ...data,
        scheduledAt: this.toUTCISOString(data.scheduledAt)
      };
      const result = await agent.TeamMatches.createDirect(payload);
      runInAction(() => { this.submitting = false; });
      toast.success((i18n as any).t('teamMatch.success.challengeSent'));
      return result.id;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      toast.error((i18n as any).t('teamMatch.errors.challengeFailed'));
      return null;
    }
  };

  createOpenChallenge = async (data: {
    teamAId: string; latitude?: number; longitude?: number; scheduledAt?: string;
  }): Promise<string | null> => {
    this.submitting = true;
    try {
      const payload = {
        ...data,
        scheduledAt: this.toUTCISOString(data.scheduledAt)
      };
      const result = await agent.TeamMatches.createOpen(payload);
      runInAction(() => { this.submitting = false; });
      toast.success((i18n as any).t('teamMatch.success.openChallengeCreated'));
      return result.id;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      return null;
    }
  };

  respondToChallenge = async (id: string, accept: boolean): Promise<boolean> => {
    this.submitting = true;
    try {
      await agent.TeamMatches.respond(id, accept);
      runInAction(() => {
        this.submitting = false;
        if (this.selectedMatch?.id === id) {
          this.selectedMatch = {
            ...this.selectedMatch,
            status: accept ? 'Accepted' : 'Rejected'
          };
        }
      });
      toast.success(accept
        ? (i18n as any).t('teamMatch.success.challengeAccepted')
        : (i18n as any).t('teamMatch.success.challengeRejected'));
      await this.loadPendingChallenges();
      return true;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      return false;
    }
  };

  applyToChallenge = async (matchId: string, teamId: string): Promise<boolean> => {
    this.submitting = true;
    try {
      await agent.TeamMatches.apply(matchId, teamId);
      runInAction(() => { this.submitting = false; });
      toast.success((i18n as any).t('teamMatch.success.applied'));
      return true;
    } catch (error: any) {
      runInAction(() => { this.submitting = false; });
      toast.error(error?.response?.data || (i18n as any).t('teamMatch.errors.applyFailed'));
      return false;
    }
  };

  acceptApplication = async (applicationId: string): Promise<boolean> => {
    this.submitting = true;
    try {
      await agent.TeamMatches.acceptApplication(applicationId);
      runInAction(() => { this.submitting = false; });
      toast.success((i18n as any).t('teamMatch.success.applicationAccepted'));
      if (this.selectedMatch) await this.loadMatch(this.selectedMatch.id);
      return true;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      return false;
    }
  };

  respondAvailability = async (matchId: string, available: boolean): Promise<boolean> => {
    this.submitting = true;
    try {
      await agent.TeamMatches.respondAvailability(matchId, available);
      runInAction(() => {
        this.submitting = false;
        if (this.selectedMatch?.id === matchId) {
          this.selectedMatch = { ...this.selectedMatch };
        }
      });
      toast.success(available
        ? (i18n as any).t('teamMatch.success.availableConfirmed')
        : (i18n as any).t('teamMatch.success.unavailableConfirmed'));
      if (this.selectedMatch) await this.loadMatch(matchId);
      return true;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      return false;
    }
  };

  reportResult = async (matchId: string, scoreA: number, scoreB: number): Promise<boolean> => {
    this.submitting = true;
    try {
      await agent.TeamMatches.reportResult(matchId, scoreA, scoreB);
      runInAction(() => { this.submitting = false; });
      toast.success((i18n as any).t('teamMatch.success.resultReported'));
      if (this.selectedMatch) await this.loadMatch(matchId);
      return true;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      return false;
    }
  };

  confirmOrDispute = async (matchId: string, confirm: boolean, reason?: string): Promise<boolean> => {
    this.submitting = true;
    try {
      await agent.TeamMatches.confirmOrDispute(matchId, confirm, reason);
      runInAction(() => { this.submitting = false; });
      toast.success(confirm
        ? (i18n as any).t('teamMatch.success.resultConfirmed')
        : (i18n as any).t('teamMatch.success.resultDisputed'));
      if (this.selectedMatch) await this.loadMatch(matchId);
      return true;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      return false;
    }
  };

  // ── Team search ──────────────────────────────────────────────────────────────

  searchTeams = async (params: {
    name?: string; sportId?: string;
    lat?: number; lng?: number; radius?: number;
  }) => {
    this.searching = true;
    try {
      const results = await agent.Teams.searchTeams(params);
      runInAction(() => {
        this.searchResults = results;
        this.searching = false;
      });
    } catch (error) {
      console.error(error);
      runInAction(() => { this.searching = false; });
    }
  };

  clearSearchResults = () => { this.searchResults = []; };
  clearSelectedMatch = () => { this.selectedMatch = undefined; };
}